import * as React from 'react';
import { Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { searchStrategy as getSearchStrategy } from './utils';
import { useDebounce } from '../../library/hooks/useDebounce';

export interface BackendSearchFieldProps {
  fieldConfig?: {
    searchStrategy?: string;
    searchSelectKey?: string;
    searchThresholdLength?: number;
    multiselect?: boolean;
  };
  value?: any;
  onChange?: (value: any) => void;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

const BackendSearchField: React.FC<BackendSearchFieldProps> = ({
  fieldConfig,
  value,
  onChange,
  placeholder,
  disabled = false,
  style,
  className,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [options, setOptions] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);

  const {
    searchStrategy,
    searchSelectKey,
    searchThresholdLength = 2,
    multiselect = false,
  } = fieldConfig || {};

  const debouncedQuery = useDebounce(searchQuery, 300);

  const handleSearch = async (searchValue: string) => {
    if (!searchValue || searchValue.length < searchThresholdLength) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
      const searchFn = getSearchStrategy(searchStrategy, searchSelectKey);
      const results = await searchFn(searchValue);

      if (Array.isArray(results) && results.length > 0) {
        const formattedResults = results.map((item: any) => ({
          value: item.key,
          label: item.label,
        }));
        setOptions(formattedResults);
      } else {
        setOptions([]);
      }
    setLoading(false);
  };

  React.useEffect(() => {
    if (debouncedQuery !== undefined && debouncedQuery !== '') {
      handleSearch(debouncedQuery);
    }
  }, [debouncedQuery]);

  const getNotFoundContent = () => {
    if (loading) {
      return <Spin size="small" />;
    }

    if (searchQuery && searchQuery.length >= searchThresholdLength) {
      return t('No results found');
    }

    return t('Type to search');
  };

  return (
    <Select
      mode={multiselect ? 'multiple' : undefined}
      showSearch
      allowClear
      placeholder={placeholder}
      filterOption={false}
      onSearch={setSearchQuery}
      onChange={onChange}
      value={value}
      options={options}
      loading={loading}
      disabled={disabled}
      style={style || { width: '100%' }}
      className={className}
      notFoundContent={getNotFoundContent()}
    />
  );
};

export default BackendSearchField;
